"use client"

import * as React from "react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { cn } from "@/lib/utils"
import type { FaqItem } from "@/types/content"

/**
 * FAQ accordion with optional category filtering.
 *
 * Categories come from the CMS as free text, so the chip row is derived from
 * whatever the data contains rather than a hard-coded list.
 */
export function FaqAccordion({
  faqs,
  showFilter = true,
  className,
}: {
  faqs: FaqItem[]
  showFilter?: boolean
  className?: string
}) {
  const categories = React.useMemo(() => {
    const found = faqs.map((faq) => faq.category).filter((c): c is string => !!c)
    return Array.from(new Set(found))
  }, [faqs])

  const [active, setActive] = React.useState<string | null>(null)

  const visible = active ? faqs.filter((faq) => faq.category === active) : faqs

  return (
    <div className={cn(className)}>
      {showFilter && categories.length > 1 ? (
        <div className="mb-8 flex flex-wrap gap-2">
          <FilterChip active={active === null} onClick={() => setActive(null)}>
            همه
          </FilterChip>
          {categories.map((category) => (
            <FilterChip
              key={category}
              active={active === category}
              onClick={() => setActive(category)}
            >
              {category}
            </FilterChip>
          ))}
        </div>
      ) : null}

      <Accordion type="single" collapsible className="mx-auto grid max-w-3xl gap-3">
        {visible.map((faq) => (
          <AccordionItem key={faq.id} value={faq.id}>
            <AccordionTrigger>{faq.question}</AccordionTrigger>
            <AccordionContent>{faq.answer}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  )
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full px-4 py-2 text-[0.8125rem] font-semibold",
        "transition-all duration-200 ease-[var(--ease-out-quint)]",
        active
          ? "bg-brand-600 text-white shadow-[0_8px_20px_-10px_rgb(34_113_198/0.8)]"
          : "bg-[var(--canvas-subtle)] text-[var(--content-muted)] ring-1 ring-[var(--hairline)] hover:text-[var(--content)]",
      )}
    >
      {children}
    </button>
  )
}
